import axios from "axios";
import UserDataPopup from "components/UserData/UserDataPopup";
import { format, parseISO } from "date-fns";
import { useState } from "react";
import { FaPencilAlt, FaStar } from "react-icons/fa";
import { HiExternalLink } from "react-icons/hi";
import {
  useRecoilState,
  useRecoilTransaction_UNSTABLE,
  useSetRecoilState,
} from "recoil";
import { getToken } from "utils/getToken";
import "../../assets/css/scrollBar.css";
import {
  appointmentIdState,
  appointmentStatusState,
  dataState,
  highlightedRowState,
  isAppointmentHistoryModalOpenState,
  mainLoaderState,
  selectedUserState,
  statusModalOpenState,
  userMobileNumberState,
} from "../../store/appointment/atoms/appointmentAtomState";

import useDynamicHeight from "store/useDynamicHeight";
import AppointmentHistoryModal from "./AppointmentHistoryModal";

//PATIENT DETAILS IMPORTS
import AddPatientModal from "./AppointmentPatientDetails/AddPatientModal";
import AppointmentPatientDetail from "./AppointmentPatientDetails/AppointmentPatientDetail";

//APPOINTMENT DETAILS IMPORTS
import AppointmentDetailsModal from "./AppointmentDetails/AppointmentDetailsModal";

//Appointment VideoCALL Imports
import CustomTimeDialog from "components/ChatStatus/CustomTimeDialog";
import SnoozeDropdown from "components/dropdown/SnoozeDropdown";
import { toast } from "react-toastify";
import {
  emptyState,
  ghostBtn,
  inlineLink,
  primaryBtn,
  select as selectCls,
  td,
  th,
  trHover,
} from "components/ui/instrument";

interface AppointmentDesktopViewProps {
  handleEditClick: (rowIndex: number) => void;
  setModalContent: (content: { url: string }) => void;
  refresh: () => void;
}

const AppointmentDesktopView = ({
  handleEditClick,
  setModalContent,
  refresh,
}: AppointmentDesktopViewProps) => {
  const [highlightedRow, setHighlightedRow] =
    useRecoilState(highlightedRowState);
  const [appointmentStatus, setAppointmentStatus] = useRecoilState(
    appointmentStatusState
  );

  const [selectedUser, setSelectedUser] = useRecoilState(selectedUserState);
  const [appointmentId, setAppointmentId] = useRecoilState(appointmentIdState);
  const [mainLoader, setMainLoader] = useRecoilState(mainLoaderState);
  const [data, setData] = useRecoilState(dataState);
  const [isStatusModalOpen, setIsStatusModalOpen] =
    useRecoilState(statusModalOpenState);
  const [isPDataModalOpen, setIsPDataModalOpen] = useState(false);

  const [source, setSource] = useState("support_portal");
  const [copiedTextIndex, setCopiedTextIndex] = useState<any>("");

  const handleCopyText = (text: string, index: number) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedTextIndex(index);
      setTimeout(() => setCopiedTextIndex(null), 2000);
    });
  };

  const handlePDataModalClose = () => {
    setIsPDataModalOpen(false);
    setSelectedUser(null);
  };

  const handlePDataModal = (row: any) => {
    setSelectedUser(row.phone_number);
    // console.log(row.Chat_started_by);
    setIsPDataModalOpen(true);
  };

  function createLinkAndOpenPrescription(row: any, rowIndex: number) {
    if (!row.phone_number) {
      return "";
    }

    const url = `/userdata?mobile_number=${row.phone_number}`;

    setHighlightedRow(rowIndex as any);
    setModalContent({ url });
  }

  const handleEditStatusClick = async (rowIndex: number) => {
    // console.log("rowIndex:", rowIndex);
    const user = data[rowIndex];
    setSelectedUser(user);
    try {
      // Make the API call to get the data for the selected user
      const response = await axios.get(
        process.env.REACT_APP_API_URL +
          `consultation/appointment/fetch/?app_id=${user.id}&source=${source}`,
        {
          headers: {
            Authorization: `Token ${getToken()}`,
          },
        }
      );

      const userData = response.data.results[0];
      // console.log(response.data.results[0]);

      setAppointmentStatus(userData.appointment_status);

      setMainLoader(false);

      setIsStatusModalOpen(true);
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };

  const [isAppointmentHistoryModalOpen, setIsAppointmentHistoryModalOpen] =
    useRecoilState(isAppointmentHistoryModalOpenState);
  const setAppointmentIdState = useSetRecoilState(appointmentIdState);
  const [userMobileNumber, setUserMobileNumber] = useRecoilState(
    userMobileNumberState
  );

  const handleAppointmentHistoryModal = useRecoilTransaction_UNSTABLE(
    ({ get, set }) =>
      (rowIndex: number) => {
        const user = get(dataState)[rowIndex];
        set(appointmentIdState, user.id);
        set(userMobileNumberState, user.phone_number);
        set(isAppointmentHistoryModalOpenState, true);
      }
  );

  const handleAppointmentHistoryModalClose = () => {
    setIsAppointmentHistoryModalOpen(false);
    setAppointmentIdState(null as any);
  };
  const { ref, isOverflowing } = useDynamicHeight([data]);

  const mapRatingToNumber = (rating: string) => {
    switch (rating) {
      case "very satisfied":
        return 3;
      case "satisfied":
        return 2;
      case "unsatisfied":
        return 1;
      default:
        return 0;
    }
  };

  // Function to map numbers back to rating strings
  const mapNumberToRating = (number: number) => {
    switch (number) {
      case 3:
        return "very satisfied";
      case 2:
        return "satisfied";
      case 1:
        return "unsatisfied";
      default:
        return "";
    }
  };

  const handleEditRatingClick = async (rowIndex: number, star: number) => {
    const updatedUser = data[rowIndex];
    const newRating = mapNumberToRating(star);
    const updatedData = data.map((item, index) =>
      index === rowIndex ? { ...item, rating: newRating } : item
    );

    setData(updatedData);
    setMainLoader(true);

    try {
      const updateData = JSON.stringify({
        appointment_id: updatedUser.id,
        rating: star,
      });

      const config = {
        method: "post",
        url: process.env.REACT_APP_API_URL + "consultation/appointment/update/",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Token ${getToken()}`,
        },
        data: updateData,
      };

      const response = await axios(config);
      setMainLoader(false);
    } catch (error) {
      console.error("Error updating rating:", error);
      setMainLoader(false);
    }
  };

  const changeVideoConsultStatus = async (rowIndex: number, value: string) => {
    const updatedUser = data[rowIndex];
    try {
      const updateData = JSON.stringify({
        appointment_id: updatedUser.id,
        video_consultation_allowed: JSON.parse(value),
      });

      const config = {
        method: "post",
        url: process.env.REACT_APP_API_URL + "consultation/appointment/update/",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Token ${getToken()}`,
        },
        data: updateData,
      };
      await axios(config);
      refresh();
      toast.success("Appointment updated successfully");
    } catch (error) {
      toast.error("Error while updating video consultation status");
    }
  };

  const [showCustomDialog, setShowCustomDialog] = useState(false);
  const [snoozeId, setSnoozeId] = useState<any>();
  const [chatId, setChatId] = useState<any>();

  function handleOpenModal(snooze_id: any, chat_id: any) {
    setShowCustomDialog(true);
    setSnoozeId(snooze_id);
    setChatId(chat_id);
  }

  // create snooze
  async function createSnooze(chatId: any, snoozeId: any, value: string) {
    const token = getToken();
    const url = `${process.env.REACT_APP_API_URL}tasks/alert/create-snooze/`;
    const data = {
      snooze_duration: value,
      alert_source_table: "appointment",
      alert_source_id: chatId,
    };
    if (value === "custom") {
      handleOpenModal(snoozeId, chatId);
    }

    try {
      if (value !== "" && value !== "custom") {
        const response = await axios.post(url, data, {
          headers: {
            Authorization: `Token ${token}`,
            "Content-Type": "application/json",
          },
        });
        if (response.status === 200 || response.status === 201) {
          toast.success(response.data.message);
          refresh();
        }
      }
    } catch (error) {
      toast.error("Error occured while creating snooze.");
    }
  }

  async function editSnooze(
    snoozeId: any,
    value: string,
    is_snoozed: any,
    chatId: any
  ) {
    const token = getToken();
    const url = `${process.env.REACT_APP_API_URL}tasks/alert/edit-snooze/`;
    const data = {
      snooze_duration: is_snoozed && value === "0_min" ? "0_min" : value,
      snooze_id: snoozeId,
    };
    if (value === "custom") {
      handleOpenModal(snoozeId, chatId);
    }
    try {
      if (value !== "" && value !== "custom") {
        const response = await axios.post(url, data, {
          headers: {
            Authorization: `Token ${token}`,
            "Content-Type": "application/json",
          },
        });
        if (response.status === 200 || response.status === 201) {
          toast.success(response.data.message);
          refresh();
        }
      }
    } catch (error) {
      toast.error("Error occured while editing snooze.");
    }
  }

  const snoozeOptions = [
    { value: "30_min", label: "30 minutes" },
    { value: "60_min", label: "60 minutes" },
    { value: "8am_next_day", label: "Next working hour (8AM next day)" },
    { value: "custom", label: "Custom" },
  ];

  async function createCustomSnooze(snooze_id: any, value: string, chat_id: any) {
    const token = getToken();
    const url =
      snooze_id && snooze_id !== ""
        ? `${process.env.REACT_APP_API_URL}tasks/alert/edit-snooze/`
        : `${process.env.REACT_APP_API_URL}tasks/alert/create-snooze/`;
    const data = {
      alert_source_table: "appointment",
      alert_source_id: chat_id,
      snooze_duration: "custom",
      snooze_end_time: value,
      snooze_id,
    };

    try {
      if (value !== "") {
        const response = await axios.post(url, data, {
          headers: {
            Authorization: `Token ${token}`,
            "Content-Type": "application/json",
          },
        });
        if (response.status === 200 || response.status === 201) {
          toast.success(response.data.message);
          refresh();
        }
      }
    } catch (error) {
      toast.error("Error occured while creating snooze.");
    }
  }


  return (
    <>
      {isPDataModalOpen && (
        <UserDataPopup
          selectedUser={selectedUser}
          noSearch={true}
          handlePDataModalClose={handlePDataModalClose}
        />
      )}
      {showCustomDialog ? (
        <CustomTimeDialog
          snoozeId={snoozeId}
          chatId={chatId}
          setOpen={setShowCustomDialog}
          createSnooze={createCustomSnooze}
        />
      ) : null}
      <table
        ref={ref as React.LegacyRef<HTMLTableElement>}
        className={`custom-scrollbar ${
          isOverflowing ? "h-[74vh]" : "h-auto"
        } max-w-full table-auto overflow-scroll rounded-lg border border-clinic-200 bg-white text-sm shadow-sm sm:block`}
      >
        <thead>
          <tr>
            <th className={th}>Doctor Name</th>
            <th className={th}>Appointment Date</th>
            <th className={th}>Type</th>
            <th className={th}>User</th>
            <th className={th}>Patient Details</th>
            {/* <th className="px-4 py-2 border">Payment-Subscription</th> */}
            <th className={th}>Status</th>
            <th className={th}>Video Consultation via App</th>
            <th className={th}>History Form</th>
            <th className={th}>Rating</th>
            <th className={th}>Actions</th>
            <th className={th}>Snooze</th>

            {/* <th className="px-4 py-2 border">Appoinment Detail</th> */}
            {/* <th className="px-4 py-2 border">Copy History</th> */}
          </tr>
        </thead>
        <tbody>
          {data.length > 0 ? (
            data.map((row, rowIndex) => (
              <tr
                style={row.is_snoozed ? { backgroundColor: "#f1f5f9" } : {}}
                key={rowIndex}
                className={
                  rowIndex === highlightedRow || row?.is_actionable
                    ? "bg-warn-50 hover:bg-warn-100"
                    : trHover
                }
              >
                <td className={`${td} tabular-nums`}>{row.doctor_name}</td>
                <td className={`${td} tabular-nums`}>
                  {row?.scheduled_at !== null ? (
                    <>
                      {format(parseISO(row.scheduled_at), "dd-MM-yyyy")}
                      <br />
                      {format(parseISO(row.scheduled_at), "HH:mm:ss")}
                    </>
                  ) : row?.preferred_time !== null ? (
                    <>
                      <p className="text-sm text-clinic-400">N/A</p>
                      <p className="text-xs font-semibold">
                        {row.preferred_time}{" "}
                        <span className="text-clinic-500">(Preferred)</span>
                      </p>
                    </>
                  ) : (
                    "N/A"
                  )}
                </td>

                <td className={`${td} tabular-nums`}>{row.appointment_type}</td>

                <td className={`${td} tabular-nums`}>
                  <div className="flex flex-col justify-center gap-3">
                    <button
                      className={`flex items-center gap-1.5 tabular-nums ${inlineLink}`}
                      onClick={() => {
                        handlePDataModal(row);
                      }}
                    >
                      {row.phone_number} <HiExternalLink />
                    </button>

                    <p>{row?.name}</p>
                  </div>
                </td>

                <td className={`w-4 table-fixed ${td}`}>
                  <AppointmentPatientDetail
                    userMobile={row?.phone_number}
                    patientId={row?.patient_id}
                    appointmentIndex={rowIndex}
                    appointmentId={row?.id}
                  />
                </td>

                {/* <td className="px-4 py-2 border">
                  {`${
                    row.payment_amount ? `₹${row.payment_amount}` : "N/A"
                  } - ${row.subscription_name ? row.subscription_name : "N/A"}`}
                </td> */}

                <td className={`${td} tabular-nums`}>
                  <p>{row.appointment_status}</p>
                  <button
                    className={`mt-2 self-center ${ghostBtn}`}
                    onClick={() => {
                      setMainLoader(true);
                      handleEditStatusClick(rowIndex);
                    }}
                  >
                    Edit Status
                  </button>
                </td>

                <td className={`flex flex-col flex-wrap ${td}`}>
                  <select
                    value={row.video_consultation_allowed}
                    onChange={(e) =>
                      changeVideoConsultStatus(rowIndex, e.target.value)
                    }
                    className={selectCls}
                  >
                    <option value={"true"}>YES</option>
                    <option value={"false"}>NO</option>
                  </select>
                </td>

                <td className={`min-w-[90px] ${td}`}>
                  {row.patient_case_history_time ? (
                    <p
                      className={`flex w-full cursor-pointer items-center justify-center gap-1 ${ghostBtn}`}
                      onClick={() => {
                        handleAppointmentHistoryModal(rowIndex);
                      }}
                    >
                      View
                      <HiExternalLink />
                    </p>
                  ) : (
                    <>
                      {row.history_form_link === "" ? (
                        <p className="text-center text-xs text-clinic-400">
                          Form Link Not Available
                        </p>
                      ) : row.history_form_link !== "" ? (
                        <p
                          className={`flex w-full cursor-pointer items-center justify-center text-center ${primaryBtn}`}
                          onClick={() =>
                            handleCopyText(row.history_form_link, rowIndex)
                          }
                        >
                          {copiedTextIndex === rowIndex
                            ? "Copied!"
                            : "Copy Form Link"}
                        </p>
                      ) : (
                        <p className="text-clinic-400">N/A</p>
                      )}
                    </>
                  )}
                </td>

                <td className={`w-[150px] ${td}`}>
                  <p>{row.rating ? row.rating : "Not Filled"}</p>

                  <div className="flex items-center gap-1">
                    {[1, 2, 3].map((star) => (
                      <FaStar
                        key={star}
                        className={`cursor-pointer ${
                          mapRatingToNumber(row.rating) >= star
                            ? "text-warn-400"
                            : "text-clinic-300"
                        }`}
                        onClick={() => {
                          handleEditRatingClick(rowIndex, star);
                        }}
                      />
                    ))}
                  </div>
                </td>

                <td
                  className={`mr-2 cursor-pointer ${td} font-medium !text-action-600 hover:underline`}
                  onClick={() => {
                    setMainLoader(true);
                    handleEditClick(rowIndex);
                  }}
                >
                  Edit All <FaPencilAlt className="relative left-3" />
                </td>
                <td className={`flex flex-col items-center gap-y-2 ${td}`}>
                  <SnoozeDropdown
                    selectedValue={row.is_snoozed ? row.snoozed_duration : ""}
                    onChange={(value) => {
                      if (row.snooze_id) {
                        editSnooze(
                          row.snooze_id,
                          value,
                          row.is_snoozed,
                          row.id
                        );
                      } else {
                        createSnooze(row.id, row.snooze_id, value);
                      }
                    }}
                    options={
                      row.is_snoozed
                        ? [
                            ...snoozeOptions,
                            { value: "0_min", label: "Remove snooze" },
                          ]
                        : snoozeOptions
                    }
                  />

                  <div>
                    {row.snoozed_until ? (
                      <p className="font-semibold tabular-nums">
                        till -{" "}
                        {format(
                          new Date(row.snoozed_until),
                          "HH:mm (dd-MM-yy)"
                        )}
                      </p>
                    ) : null}
                  </div>
                  {row.snoozed_by ? (
                    <p>{row.snoozed_by.replace("@gagahealth.com", "")}</p>
                  ) : null}
                </td>
                {/* <td className="px-4 py-2 border">
                  <AppointmentDetails
                    appointmentDetails={row?.appointment_details}
                    appointmentId={row?.id}
                  />
                </td> */}
                {/* <td className="px-4 py-2 mr-2 text-blue-500 border cursor-pointer">
                  <CopyToClipboard
                    appointmentDetails={row?.appointment_details}
                    userMobile={row?.phone_number}
                    patientId={row?.patient_id}
                    appointmentIndex={rowIndex}
                  />
                </td> */}
              </tr>
            ))
          ) : (
            <tr>
              <td className={emptyState} colSpan={11}>
                No appointments match the selected filters.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <AddPatientModal />
      <AppointmentDetailsModal />
      {isAppointmentHistoryModalOpen && (
        <AppointmentHistoryModal
          isOpen={isAppointmentHistoryModalOpen}
          closeModal={handleAppointmentHistoryModalClose}
        />
      )}
    </>
  );
};

export default AppointmentDesktopView;
